import React, { useImperativeHandle, useRef } from "react";
import { ScrollArea } from "@base-ui/react/scroll-area";
import "./sidebar-content.css";

export interface ISidebarContentRef {
	/** The container of the children */
	el: HTMLDivElement | null;
	/** The scrollable parent, null if not scrollable */
	scrollableEl: HTMLElement | null;
}

export function SidebarContent({
	children,
	className,
	ref,
	style,
	scrollable = false,
	fade = false,
	noPadding = false,
}: {
	children: React.ReactNode;
	className?: string;
	ref?: React.Ref<ISidebarContentRef>;
	style?: React.CSSProperties;
	scrollable?: boolean;
	fade?: boolean;
	noPadding?: boolean;
}) {
	const contentRef = useRef<HTMLDivElement>(null);

	useImperativeHandle(
		ref,
		() => ({
			get el() {
				return contentRef.current;
			},
			get scrollableEl() {
				return scrollable ? contentRef.current?.parentElement ?? null : null;
			},
		}),
		[scrollable],
	);

	const contentClassName = ["sidebar-content", noPadding ? "sidebar-content--no-padding" : ""]
		.filter(Boolean)
		.join(" ");

	if (scrollable) {
		const scrollAreaClassName = [
			"sidebar-content__scroll-area",
			fade ? "sidebar-content__scroll-area--fade" : "",
			className || "",
		]
			.filter(Boolean)
			.join(" ");

		return (
			<ScrollArea.Root className={scrollAreaClassName} style={style}>
				<ScrollArea.Viewport className="sidebar-content__scroll-area-viewport">
					<div className={contentClassName} ref={contentRef}>
						{children}
					</div>
				</ScrollArea.Viewport>
				<ScrollArea.Scrollbar className="sidebar-content__scrollbar" orientation="vertical">
					<ScrollArea.Thumb className="sidebar-content__scrollbar-thumb" />
				</ScrollArea.Scrollbar>
			</ScrollArea.Root>
		);
	}

	return (
		<div
			ref={contentRef}
			className={[contentClassName, className || ""].filter(Boolean).join(" ")}
			style={style}
		>
			{children}
		</div>
	);
}
